import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { Close, SearchRounded } from "@mui/icons-material";
import InexButton from "../../../InexButton";
import { useAuthToken } from "../../../../hooks/auth";
import {
  useSearchPraticesQuery,
  useSetInfosProMutation,
} from "../../../../store/api/user";
import { useIsMobile } from "../../../../hooks/responsive";

const PraticesDialog = ({ open, onClose, pratices }) => {
  const isMobile = useIsMobile();

  const [currentPratices, setCurrentPratices] = React.useState(pratices);

  const diff = React.useMemo(() => {
    return JSON.stringify(currentPratices) !== JSON.stringify(pratices);
  }, [pratices, currentPratices]);

  const [query, setQuery] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);
  const authToken = useAuthToken();

  const { data = { pratiques: [] } } = useSearchPraticesQuery(
    {
      authToken,
      query,
    },
    {
      skip: !authToken || !query || query.length < 2,
    },
  );

  const textFieldRef = React.useRef(null);

  const textFieldSize = React.useMemo(() => {
    return textFieldRef.current?.getBoundingClientRect()?.width ?? 200;
  }, [textFieldRef.current]);

  const [setInfosProMutation] = useSetInfosProMutation();

  const handleValidate = async () => {
    // 1. On gère le "skip" manuellement
    if (!authToken) {
      console.warn("Action annulée : Aucun token d'authentification.");
      return;
    }

    try {
      // 2. On déclenche la mutation
      // .unwrap() permet de tomber dans le catch en cas d'erreur (403, 504, etc.)
      await setInfosProMutation({
        authToken,
        jsonPratiques: currentPratices,
      }).unwrap();

      // 3. Succès : On ferme la modale
      onClose();

    } catch (err) {
      // 4. Gestion des erreurs Cloud Run
      console.error("Échec de la mutation :", err);
    }
  };

  return (
    <Dialog
      open={open}
      sx={{
        backgroundColor: (theme) => theme.palette.blackPro + "99",
      }}
      maxWidth="xl"
      PaperProps={{
        sx: {
          backgroundColor: (theme) => theme.palette.blackProLighter,
          color: "white",
          boxShadow: (theme) => theme.shadows[27],
          borderRadius: "15px",
          paddingLeft: isMobile ? 1 : 5,
          paddingRight: isMobile ? 1 : 5,
        },
      }}
    >
      <DialogTitle
        sx={{ m: 0, p: 2, ml: 3, mr: 3, textAlign: "center" }}
        id="customized-dialog-title"
      >
        <Typography variant={"h3"}>
          {"Sélectionnez une ou plusieurs pratiques"}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <Close />
      </IconButton>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TextField
            size={"small"}
            ref={textFieldRef}
            placeholder={
              currentPratices.length >= 3
                ? "Nombre maximum de pratiques enregistré"
                : "Rechercher une pratique"
            }
            disabled={currentPratices.length >= 3}
            sx={{
              backgroundColor: "#C7C4DE",
              borderRadius: "30px",
              outline: "none",
            }}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
            }}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            variant="standard"
            autoComplete="off"
            autoCorrect={"off"}
            slotProps={{
              input: {
                sx: {
                  outline: "none",
                  padding: 1,
                  "&::before": {
                    display: "none",
                  },
                  "&::after": {
                    display: "none",
                  },
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded
                      sx={{
                        marginLeft: 0.5,
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Popover
            open={
              isFocused &&
              data?.pratiques?.length > 0 &&
              currentPratices?.length < 3 && query.length > 1
            }
            anchorEl={textFieldRef.current}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            disableAutoFocus={true}
            disableEnforceFocus={true}
            PaperProps={{
              sx: {
                marginTop: 1,
                width: textFieldSize,
                maxHeight: "300px",
              },
            }}
          >
            {data?.pratiques
              .filter((pratique) => {
                return !currentPratices.some((p) => p.id === pratique.id);
              })
              .map((pratique) => {
                return (
                  <MenuItem
                    key={pratique.id}
                    onClick={() => {
                      if (pratices.length < 3) {
                        setCurrentPratices([...currentPratices, pratique]);
                        setQuery("");
                      }
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      button: {
                        opacity: "0",
                      },
                      "&:hover": {
                        button: {
                          opacity: "1",
                        },
                      },
                    }}
                  >
                    {pratique.titre}
                    <Button
                      sx={{
                        textTransform: "none",
                      }}
                    >
                      Ajouter
                    </Button>
                  </MenuItem>
                );
              })}
          </Popover>

          <Typography
            sx={{
              mt: 5,
              mb: 2,
              color: "white",
              fontSize: "18px",
              fontWeight: "900",
            }}
          >
            Vos pratiques sélectionnées
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            {currentPratices?.length === 0 && (
              <Typography
                sx={{
                  color: "white",
                  opacity: 0.5,
                  fontSize: "18px",
                  fontStyle: "italic",
                }}
              >{`Rechercher vos pratiques depuis la barre de recherche, elles s’afficheront ici.`}</Typography>
            )}
            {currentPratices?.map((pratice) => {
              return (
                <Chip
                  key={pratice.id}
                  label={pratice.titre}
                  onDelete={() => {
                    setCurrentPratices(
                      currentPratices.filter((pratique) => {
                        return pratice.id !== pratique.id;
                      }),
                    );
                  }}
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "16px",
                    "& > svg": {
                      fill: "white",
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <InexButton
          text={"Annuler"}
          variant={"text"}
          textWithGradient={false}
          onClick={onClose}
          sx={{
            zIndex: 1,
            marginBottom: 3,
            marginTop: 3,
            height: "50px",
            minWidth: "100px",
            background: "white",
            color: "#202020",
            border: "none",
            "&:hover": {
              border: "none",
              backgroundColor: (theme) => theme.palette.primary.main,
              "& > p": {
                color: "white",
              },
            },
          }}
        />
        <InexButton
          text={"Valider"}
          variant={"text"}
          textWithGradient={false}
          onClick={handleValidate}
          disabled={!diff}
          sx={{
            zIndex: 1,
            marginBottom: 3,
            marginTop: 3,
            height: "50px",
            minWidth: "100px",
            background: (theme) =>
              !diff ? theme.palette.grey[400] : theme.palette.primary.main,
            color: "white",
            border: "none",
            "&:hover": {
              border: "none",
              backgroundColor: "white",
              "& > p": {
                color: (theme) => theme.palette.primary.main,
              },
            },
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default PraticesDialog;
